import { useMemo, useCallback } from 'react'
import useSubmitDentalDetails from './useSubmitDentalDetails'
import useDentalFormValidation from './useDentalFormValidation'
import { UseToastOptions } from '@chakra-ui/react'

interface FormSubmissionProps {
  uid: string
  toast: (options: UseToastOptions) => void
  formValues: {
    currentNumAmalgamFillings: string
    amalgamTotalTeeth: string
    firstAmalgamAge: string
    lastAmalgamAge: string
    currentRootCanals: string
    rootCanalsTotal: string
    firstRootCanalAge: string
    lastRootCanalAge: string
    removedTeeth: string
    firstToothExtractedAge: string
    cavitChecked: string
    cavitations: string
    currentImplants: string
    implantTotalTeeth: string
    firstDentalImplantAge: string
    lastDentalImplantAge: string
  }
  isSaved: boolean
  prepareDentalDetails: () => any
  setIsSaved: (isSaved: boolean) => void
}

/**
 * Hook to manage form submission and save button state
 */
export default function useFormSubmission({
  uid,
  toast,
  formValues,
  isSaved,
  prepareDentalDetails,
  setIsSaved,
}: FormSubmissionProps) {
  const { validateForm } = useDentalFormValidation(toast)
  const { submit, loading } = useSubmitDentalDetails({}, uid)

  // Determine if the save button should be disabled
  const allFieldsFilled = useMemo(() => {
    const {
      currentNumAmalgamFillings,
      amalgamTotalTeeth,
      firstAmalgamAge,
      lastAmalgamAge,
      currentRootCanals,
      rootCanalsTotal,
      firstRootCanalAge,
      lastRootCanalAge,
      removedTeeth,
      firstToothExtractedAge,
      cavitChecked,
      cavitations,
      currentImplants,
      implantTotalTeeth,
      firstDentalImplantAge,
      lastDentalImplantAge,
    } = formValues

    return (
      isSaved &&
      currentNumAmalgamFillings !== '' &&
      amalgamTotalTeeth !== '' &&
      firstAmalgamAge !== '' &&
      lastAmalgamAge !== '' &&
      currentRootCanals !== '' &&
      rootCanalsTotal !== '' &&
      firstRootCanalAge !== '' &&
      lastRootCanalAge !== '' &&
      removedTeeth !== '' &&
      firstToothExtractedAge !== '' &&
      (cavitChecked === 'No' ||
        (cavitChecked === 'Yes' && cavitations !== '')) &&
      currentImplants !== '' &&
      implantTotalTeeth !== '' &&
      firstDentalImplantAge !== '' &&
      lastDentalImplantAge !== ''
    )
  }, [formValues, isSaved])

  // Handle form submission
  const handleSave = useCallback(async () => {
    // Validate form data
    const isValid = validateForm(formValues)

    if (!isValid) {
      return
    }

    // Create the dental details object right before submitting
    const detailsToSave = prepareDentalDetails()
    detailsToSave.isSaved = true // Explicitly set isSaved to true

    await submit(detailsToSave)
    setIsSaved(true)
  }, [validateForm, formValues, prepareDentalDetails, submit, setIsSaved])

  return {
    handleSave,
    loading,
    allFieldsFilled,
  }
}
